import { toast } from 'sonner';
import { Crosshair, Trash2, Play, FileText } from 'lucide-react';
import { AquaLeakFind } from './AquaLeakFind';
import { leakPdf } from '../lib/leakPdf';
import { mkLeak } from '../lib/aquaTrain';
import { AquaTrain } from './AquaTrain';
import { getVis, setVis } from '../lib/vis';
import { simulate, leakCoef } from '../lib/aquaNet';
import { useAqua, setAqua } from '../lib/aquaStore';
import { nearestOnPipes } from './networkLayers';

const rerun = () => setTimeout(() => window.dispatchEvent(new Event('giss-aqua-rerun')), 60);
const nf = (v, d = 2) => (v == null || !Number.isFinite(+v) ? '–' : (+v).toFixed(d).replace('.', ','));

// leak tab: mark leaks on the pipes (map centre) with a fixed outflow or a hole size (pressure-dependent) and simulate them
export function AquaLeaks({ t, project, updateProject, cfg, setCfg }) {
  const s = useAqua(), leaks = (project.fittings || []).filter((f) => f.type === 'leak');
  const patchLeak = (id, h) => { updateProject((p) => ({ ...p, fittings: p.fittings.map((f) => (f.id === id ? { ...f, hyd: { ...(f.hyd || {}), ...h } } : f)) })); rerun(); };
  const add = () => {
    const map = window.__gissMapView?.()?.m;
    if (!map) return;
    const hit = nearestOnPipes(map, map.getCenter(), (project.pipes || []).filter((p) => p.type === 'water' || !p.type), project.fittings || [], 60);
    if (!hit) { toast.error(t.aqLeakNoPipe); return; }
    const pp = project.pipes.find((p) => p.id === hit.pipeId);
    const f = mkLeak(project, hit.pt, pp, `${t.aqLeakName} ${leaks.length + 1}`);
    // a leak placed here must be visible even if leaks were hidden in the object filter
    if (getVis().leaks === false) setVis({ leaks: true });
    updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), f] }));
    toast.success(`${t.aqLeakAdded}: ${pp.name || ''}`);
    map.panTo(hit.pt);
    rerun();
  };
  const del = (id) => { updateProject((p) => ({ ...p, fittings: p.fittings.filter((f) => f.id !== id) })); rerun(); };
  const calc = async () => { const r = await simulate(project, project.aqua || {}); if (r.error) toast.error(t.aqHydCalcErr); else setAqua({ res: r, on: true }); };
  const res = s.res && !s.res.error ? s.res : null;
  return (
    <div className="aq-leaks" data-testid="aq-leaks">
      <p className="muted">{t.aqLeakIntro}</p>
      <div className="row">
        <button className="btn btn-red btn-sm" data-testid="aq-leak-add" onClick={add}><Crosshair size={14} /> {t.aqLeakAdd}</button>
        <button className="btn btn-dark btn-sm" data-testid="aq-leak-calc" onClick={calc}><Play size={14} /> {t.aqRun}</button>
        {leaks.length > 0 && <button className="btn btn-outline btn-sm" data-testid="aq-leak-pdf" onClick={() => leakPdf(t, project).catch((e) => toast.error(String(e?.message || e)))}><FileText size={14} /> {t.lrBtn}</button>}
      </div>
      {res && <p className="aq-kpi" data-testid="aq-leak-total"><b>{t.aqLeakTotal}: {nf(res.leakQ)} l/s ({nf((res.leakQ || 0) * 3.6, 1)} m³/h · {nf((res.leakQ || 0) * 86.4, 1)} m³/Tag)</b> · {t.aqInflow}: {nf(res.inflow)} l/s</p>}
      {!leaks.length && <p className="muted" data-testid="aq-leak-empty">{t.aqLeakNone}</p>}
      {leaks.map((f, i) => {
        const h = f.hyd || {}, r = res?.leaks?.find((x) => x.fid === f.id), hole = h.lmode === 'hole';
        return (
          <div key={f.id} className={`aq-leak ${h.leakOn ? 'on' : ''}`} data-testid={`aq-leak-${i}`}>
            <div className="row">
              <b>💧 {f.name}</b>
              <label className="aq-inline"><input type="checkbox" data-testid={`aq-leak-on-${i}`} checked={!!h.leakOn} disabled={!f.hostPipe} onChange={(e) => patchLeak(f.id, { leakOn: e.target.checked })} /> {t.aqLeakActive}</label>
              <button className="btn btn-outline btn-sm" data-testid={`aq-leak-show-${i}`} onClick={() => window.dispatchEvent(new CustomEvent('giss-select-fit', { detail: f.id }))}>{t.aqLeakShow}</button>
              <button className="icon-btn danger" data-testid={`aq-leak-del-${i}`} aria-label={t.pitDelete} onClick={() => del(f.id)}><Trash2 size={14} /></button>
            </div>
            {!f.hostPipe && <small className="aq-warn">{t.aqLeakNoHost}</small>}
            <div className="row">
              <span className="chip-group">
                <button type="button" className={`chip seg ${!hole ? 'on' : ''}`} data-testid={`aq-leak-fixed-${i}`} onClick={() => patchLeak(f.id, { lmode: 'fixed' })}>{t.aqLeakFixed}</button>
                <button type="button" className={`chip seg ${hole ? 'on' : ''}`} data-testid={`aq-leak-hole-${i}`} onClick={() => patchLeak(f.id, { lmode: 'hole' })}>{t.aqLeakHole}</button>
              </span>
              {hole
                ? <label>{t.aqLeakD}<input type="number" step="0.5" data-testid={`aq-leak-d-${i}`} value={h.ld ?? 5} onChange={(e) => patchLeak(f.id, { ld: e.target.value })} /> mm</label>
                : <label>{t.aqLeakOutQ}<input type="number" step="0.05" data-testid={`aq-leak-q-${i}`} value={h.lq ?? 0.5} onChange={(e) => patchLeak(f.id, { lq: e.target.value })} /> l/s</label>}
            </div>
            {hole && <small className="muted">{t.aqLeakCoef}: {nf(leakCoef(f), 4)} l/s·m^-0,5</small>}
            {r && <small data-testid={`aq-leak-res-${i}`}>{t.aqLeakOut}: <b>{nf(r.q)} l/s</b> ({nf(r.q * 3.6, 1)} m³/h) · {t.aqLeakP}: {nf(r.bar, 2)} bar</small>}
            {r && Number.isFinite(r.bar) && r.bar < 0 && <small className="aq-warn" data-testid={`aq-leak-neg-${i}`}>⚠️ {t.aqLeakNeg}</small>}
          </div>
        );
      })}
      <AquaTrain t={t} project={project} updateProject={updateProject} cfg={cfg} />
      <AquaLeakFind t={t} project={project} cfg={cfg} setCfg={setCfg} />
    </div>
  );
}
